import {$intent} from '../../intent/constants';
import {
  lazyGlobalStyleIntent,
  lazyStyleIntent,
  runtimeStyleIntent,
} from '../../style/intents';
import {stylexIntent} from '../../stylex/intent';
import {assignTokensIntent} from '../../token/assignTokens';
import {$address, $token} from '../../token/constants';
import {lazyGlobalTokenIntent, lazyTokenIntent} from '../../token/intents';
import type {IntentOwner, IntentSource, ViewInspection} from './index';
import type {
  InspectionSnapshot,
  InspectionValue,
  SerializedIntentRecord,
} from './types';

const MAX_VALUES = 2000;
const MAX_DEPTH = 20;
const MAX_STRING_LENGTH = 10000;
const MAX_CHARACTERS = 256 * 1024;

/** Tokens can inherit data properties; never invoke accessors or conversion hooks. */
function readDataProperty(object: object, key: PropertyKey): unknown {
  let current: object | null = object;
  for (let depth = 0; current && depth < MAX_DEPTH; depth++) {
    const descriptor = Object.getOwnPropertyDescriptor(current, key);
    if (descriptor) {
      return 'value' in descriptor ? descriptor.value : undefined;
    }
    current = Object.getPrototypeOf(current);
  }
  return undefined;
}

/** Bounded copying, never JSON.stringify on application values. */
export function serializeInspection(
  inspection: ViewInspection,
  sessionId: string,
  instanceId: number,
  revision: number,
): InspectionSnapshot {
  let complete = inspection.complete;
  let metadataTruncated = false;
  let remainingValues = MAX_VALUES;
  // Reserve the Toolkit message wrapper, then charge for JSON escaping
  // and fixed structure. This avoids encoding the snapshot to measure it.
  let remainingCharacters = MAX_CHARACTERS - 2048;
  const ancestors = new Set<object>();

  function takeCharacters(count: number): boolean {
    if (count > remainingCharacters) {
      remainingCharacters = 0;
      return false;
    }
    remainingCharacters -= count;
    return true;
  }

  function metadataString(value: string): string {
    const limit = Math.min(
      MAX_STRING_LENGTH,
      Math.max(0, Math.floor((remainingCharacters - 2) / 6)),
    );
    let result = value;
    if (value.length > limit) {
      metadataTruncated = true;
      result = limit ? `${value.slice(0, limit - 1)}…` : '';
    }
    takeCharacters(result.length * 6 + 2);
    return result;
  }

  function cloneOwner(owner: IntentOwner): IntentOwner {
    return Object.freeze({
      instanceId: owner.instanceId,
      view: metadataString(owner.view),
      config: metadataString(owner.config),
    });
  }

  // Snapshot structure; copy identity before declarations consume the budget.
  takeCharacters(1024);
  const owner = cloneOwner(inspection.owner);
  const subview = inspection.subview
    ? Object.freeze({
        ownerInstanceId: inspection.subview.ownerInstanceId,
        name: metadataString(inspection.subview.name),
      })
    : null;

  function unavailable(reason: string): InspectionValue {
    complete = false;
    return Object.freeze({type: 'unavailable', reason});
  }

  function serialize(value: unknown, depth = 0): InspectionValue {
    // Reserve the wrapper, scalar, and possible truncation marker.
    if (remainingValues-- <= 0 || depth > MAX_DEPTH || !takeCharacters(128)) {
      return unavailable('limit');
    }
    if (value === null || typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.length <= MAX_STRING_LENGTH &&
        takeCharacters(value.length * 6)
        ? value
        : unavailable('limit');
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : unavailable('non-finite number');
    }
    if (typeof value !== 'object') {
      return unavailable(typeof value);
    }
    if (ancestors.has(value)) {
      return unavailable('circular');
    }
    try {
      if (readDataProperty(value, $token)) {
        const address = readDataProperty(value, $address);
        if (address && typeof address === 'object') {
          const path = readDataProperty(address, 'path');
          const id = readDataProperty(address, 'id');
          if (
            typeof path === 'string' &&
            typeof id === 'string' &&
            path.length <= MAX_STRING_LENGTH &&
            id.length <= MAX_STRING_LENGTH &&
            takeCharacters((path.length + id.length) * 6)
          ) {
            return Object.freeze({type: 'token', path, id});
          }
        }
        return unavailable(remainingCharacters ? 'unaddressed token' : 'limit');
      }
      ancestors.add(value);
      const read = (key: string) => {
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        if (descriptor && 'value' in descriptor) {
          return serialize(descriptor.value, depth + 1);
        }
        remainingValues--;
        return unavailable(takeCharacters(128) ? 'accessor' : 'limit');
      };
      if (Array.isArray(value)) {
        const items: InspectionValue[] = [];
        for (
          let i = 0;
          i < value.length && remainingValues > 0 && remainingCharacters > 0;
          i++
        ) {
          items.push(read(String(i)));
        }
        const truncated = items.length < value.length;
        if (truncated) {
          items.push(unavailable('limit'));
        }
        return Object.freeze({
          type: 'array',
          items: Object.freeze(items),
          ...(truncated && {truncated: true as const}),
        });
      }
      const prototype = Object.getPrototypeOf(value);
      if (prototype !== null && prototype !== Object.prototype) {
        return unavailable('unsupported object');
      }
      const entries: Record<string, InspectionValue> = Object.create(null);
      let truncated = false;
      for (const key of Object.keys(value)) {
        if (
          remainingValues <= 0 ||
          key.length > MAX_STRING_LENGTH ||
          !takeCharacters(key.length * 6 + 4)
        ) {
          truncated = true;
          complete = false;
          break;
        }
        entries[key] = read(key);
      }
      return Object.freeze({
        type: 'object',
        entries: Object.freeze(entries),
        ...(truncated && {truncated: true as const}),
      });
    } catch {
      return unavailable('unreadable');
    } finally {
      ancestors.delete(value);
    }
  }

  function cloneSource(
    source: IntentSource,
    depth = 0,
  ): IntentSource | undefined {
    // Source and owner structure; strings are charged separately.
    if (!takeCharacters(256)) {
      metadataTruncated = true;
      return undefined;
    }
    if (depth >= MAX_DEPTH && source.parent) {
      metadataTruncated = true;
    }
    let value = source.value;
    if (typeof value === 'number' && !Number.isFinite(value)) {
      complete = false;
      value = undefined;
    }
    const owner = cloneOwner(source.owner);
    const name =
      source.name !== undefined ? metadataString(source.name) : undefined;
    if (
      typeof value === 'string' &&
      (value.length > MAX_STRING_LENGTH || !takeCharacters(value.length * 6))
    ) {
      metadataTruncated = true;
      value = undefined;
    }
    const parent =
      source.parent && depth < MAX_DEPTH
        ? cloneSource(source.parent, depth + 1)
        : undefined;
    return Object.freeze({
      type: source.type,
      owner,
      ...(name !== undefined && {name}),
      ...(value !== undefined && {value}),
      ...(parent && {parent}),
    });
  }

  const records: SerializedIntentRecord[] = [];
  for (let id = 0; id < inspection.records.length && id < MAX_VALUES; id++) {
    // Record structure, including a fallback unavailable value.
    if (!takeCharacters(512)) {
      break;
    }
    const {intent, source, priority, inherited} = inspection.records[id];
    const serializedSource = source ? cloneSource(source) ?? null : null;
    let type: SerializedIntentRecord['type'] = 'opaque';
    let value: InspectionValue;
    let compiled: boolean | undefined;
    let intentType = 'unknown';
    try {
      const symbol = readDataProperty(intent, $intent);
      intentType = metadataString(
        typeof symbol === 'symbol'
          ? symbol.description ?? 'unknown'
          : 'unknown',
      );
      if (symbol === lazyStyleIntent.symbol) {
        type = 'css';
        value = serialize(readDataProperty(intent, '0'));
        compiled = !!readDataProperty(intent, '1');
      } else if (symbol === lazyGlobalStyleIntent.symbol) {
        type = 'global-css';
        value = serialize(intent);
      } else if (
        symbol === lazyTokenIntent.symbol ||
        symbol === lazyGlobalTokenIntent.symbol ||
        symbol === assignTokensIntent.symbol
      ) {
        if (symbol === assignTokensIntent.symbol) {
          type = 'token-assignment';
        } else {
          type = symbol === lazyTokenIntent.symbol ? 'tokens' : 'global-tokens';
        }
        value = serialize(intent);
      } else if (symbol === runtimeStyleIntent.symbol) {
        type = 'runtime-css';
        value = unavailable('runtime styles only');
      } else if (symbol === stylexIntent.symbol) {
        type = 'compiled-css';
        compiled = true;
        value = unavailable('compiled styles only');
      } else {
        // Props, providers, and config bindings can carry arbitrary app data.
        value = unavailable('unsupported intent');
      }
    } catch {
      value = unavailable('unreadable');
    }
    records.push(
      Object.freeze({
        id,
        intentType,
        type,
        value,
        source: serializedSource,
        priority,
        inherited: inherited ?? false,
        ...(compiled !== undefined && {compiled}),
      }),
    );
  }
  const recordsTruncated = records.length < inspection.records.length;
  return Object.freeze({
    version: 1,
    sessionId,
    instanceId,
    revision,
    inspection: Object.freeze({
      owner,
      subview,
      complete: complete && !recordsTruncated && !metadataTruncated,
      metadataTruncated,
      records: Object.freeze(records),
      recordsTruncated,
    }),
  });
}
